import { useState } from 'react';

import {
  InfoModal,
  DimensionScoreLevelName,
  BadgeCircle,
  CriteriaText,
  DimensionScorePill,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import CheckIcon from '@mui/icons-material/Check';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Box, Typography, TextField, Checkbox, SxProps, Theme, Button } from '@mui/material';

import type { EditingDimensionData } from '../assessMyTrack.types';

const EDIT_MODAL_STRINGS = appStrings.modules.assessMyTrack.timelineChart.editModal;
const SCORE_LABELS = appStrings.modules.assessMyTrack.scorecardLevels;

const SCORE_OPTIONS: readonly DimensionScoreLevelName[] = ['advanced', 'minimum', 'unmet'] as const;

const SCORE_DISPLAY_LABELS: Record<DimensionScoreLevelName, string> = {
  advanced: SCORE_LABELS.advanced,
  minimum: SCORE_LABELS.proficient,
  unmet: SCORE_LABELS.notYet,
};

type CriteriaDescriptions = Readonly<{
  advanced?: string;
  minimum?: string;
}>;

type DimensionEditModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  dimension: EditingDimensionData;
  onSubmit: (newScore: DimensionScoreLevelName, justification: string) => void;
  criteriaDescriptions?: CriteriaDescriptions;
  isSubmitting?: boolean;
  hasSubmitError?: boolean;
}>;

const getCriteriaForScore = (
  score: DimensionScoreLevelName,
  criteriaDescriptions?: CriteriaDescriptions,
): string | undefined => {
  if (score === 'unmet') return EDIT_MODAL_STRINGS.notYetDescription;
  if (score === 'advanced') return criteriaDescriptions?.advanced;
  if (score === 'minimum') return criteriaDescriptions?.minimum;

  return undefined;
};

export const DimensionEditModal = ({
  open,
  onClose,
  dimension,
  onSubmit,
  criteriaDescriptions,
  isSubmitting = false,
  hasSubmitError = false,
}: DimensionEditModalProps) => {
  const [selectedScore, setSelectedScore] = useState<DimensionScoreLevelName>(
    dimension?.currentScore ?? 'unmet',
  );
  const [justification, setJustification] = useState('');

  const hasScoreChanged = dimension && selectedScore !== dimension.currentScore;

  const handleSubmit = () => {
    onSubmit(selectedScore, justification);
  };

  return (
    <InfoModal
      open={open}
      onClose={onClose}
      width="sm"
      featuredIcon={{
        iconName: 'CheckCircle',
        size: 'lg',
        color: 'Colors/Foreground/fg-secondary',
      }}
      headerContent={
        <Box>
          <Typography variant="text-md-semibold" color={({ backrs }) => backrs.colors['Gray/900']}>
            {dimension?.dimensionName}
          </Typography>
          <Typography variant="text-sm-regular" color={({ backrs }) => backrs.colors['Gray/600']}>
            {EDIT_MODAL_STRINGS.subtitle}
          </Typography>
        </Box>
      }
      bodyContainerSx={bodyContainerOverrideSx}
      bodyContent={
        <Box sx={modalBodySx}>
          <Box sx={scoreCardsContainerSx}>
            {SCORE_OPTIONS.map((score) => {
              const isSelected = selectedScore === score;
              const isLubavAssessed = dimension?.lubavScore === score;
              const criteria = getCriteriaForScore(score, criteriaDescriptions);

              return (
                <Box
                  key={score}
                  id={`assess-track-dimension-score-card-${score}`}
                  sx={scoreCardSx(isSelected)}
                  onClick={() => setSelectedScore(score)}
                >
                  <Box sx={cardHeaderRowSx}>
                    <Box sx={cardHeaderLeftSx}>
                      <DimensionScorePill score={score} label={SCORE_DISPLAY_LABELS[score]} />
                      {isLubavAssessed && (
                        <Box sx={lubavBadgeSx}>
                          <BadgeCircle size="md" />
                          <Typography variant="text-xs-medium" sx={lubavTextSx}>
                            {EDIT_MODAL_STRINGS.lubavAssessment}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Checkbox
                      id={`assess-track-dimension-score-${score}`}
                      checked={isSelected}
                      onChange={() => setSelectedScore(score)}
                      icon={<CheckBoxOutlineBlankIcon />}
                      checkedIcon={<CheckIcon />}
                    />
                  </Box>
                  {criteria && <CriteriaText text={criteria} textSx={criteriaTextSx} />}
                </Box>
              );
            })}
          </Box>

          {hasScoreChanged && (
            <Box>
              <Typography
                variant="text-sm-medium"
                color={({ backrs }) => backrs.colors['Gray/700']}
              >
                {EDIT_MODAL_STRINGS.justificationLabel}
              </Typography>
              <TextField
                id="assess-track-dimension-justification-input"
                multiline
                rows={3}
                fullWidth
                placeholder={EDIT_MODAL_STRINGS.justificationPlaceholder}
                value={justification}
                sx={textFieldSx}
                onChange={(e) => setJustification(e.target.value)}
              />
            </Box>
          )}

          {hasSubmitError && (
            <Typography variant="text-sm-regular" sx={submitErrorSx}>
              {EDIT_MODAL_STRINGS.submitError}
            </Typography>
          )}

          <Button
            hierarchy="Primary"
            size="md"
            fullWidth
            onClick={handleSubmit}
            disabled={!hasScoreChanged || isSubmitting}
            id="assess-track-dimension-submit-button"
          >
            {EDIT_MODAL_STRINGS.submitButton}
          </Button>
        </Box>
      }
    />
  );
};

const bodyContainerOverrideSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  zIndex: 1,
  px: backrs.spacing['spacing-3xl'],
  pb: backrs.spacing['spacing-3xl'],
});

const modalBodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const scoreCardsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const scoreCardSx =
  (isSelected: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-sm'],
    padding: backrs.spacing['spacing-lg'],
    borderRadius: backrs.radius['radius-lg'],
    border: `1.5px solid ${isSelected ? backrs.colors['Brand/500'] : backrs.colors['Colors/Border/border-secondary']}`,
    cursor: 'pointer',
    '&:hover': {
      borderColor: backrs.colors['Brand/300'],
    },
  });

const cardHeaderRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const cardHeaderLeftSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
});

const lubavBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const lubavTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
});

const criteriaTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
});

const textFieldSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-md'],
  '& .MuiInputBase-input': {
    typography: 'text-sm-regular',
  },
});

const submitErrorSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
});
